import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useWishlist } from "../../context/WishlistContext";
import SearchOverlay from "./SearchOverlay";

const shopLinks = [
  { label: "Home Backup", href: "https://ironhavensupply.com/collections/home-backup-power" },
  { label: "Solar Generators", href: "https://ironhavensupply.com/collections/solar-generators" },
  { label: "Off-Grid Systems", href: "https://ironhavensupply.com/collections/off-grid-power-systems" },
  { label: "Preparedness", href: "https://ironhavensupply.com/collections/survival-gear-emergency-preparedness" },
];

const resourceLinks = [
  { label: "Shop All Products", to: "/shop", icon: "ri-store-2-line" },
  { label: "Compare Systems", to: "/compare-backup-power-systems", icon: "ri-equalizer-2-line" },
  { label: "Side-by-Side Compare", to: "/compare", icon: "ri-layout-column-line" },
  { label: "FAQ", to: "/faq", icon: "ri-question-answer-line" },
  { label: "Installation Guides", to: "/installation-guides", icon: "ri-tools-line" },
  { label: "Warranty Info", to: "/warranty", icon: "ri-shield-check-line" },
  { label: "MAP Policy", to: "/map-policy", icon: "ri-price-tag-3-line" },
  { label: "Order Tracking", to: "/order-tracking", icon: "ri-truck-line" },
  { label: "Authorized Brands", to: "/authorized-brands", icon: "ri-verified-badge-line" },
  { label: "EcoFlow — Coming Soon", to: "/ecoflow", icon: "ri-flashlight-line" },
  { label: "About Us", to: "/about", icon: "ri-information-line" },
  { label: "Contact", to: "/contact", icon: "ri-mail-line" },
];

const FIND_MY_SYSTEM_URL = "https://ironhavensupply.com/pages/home-backup-power-systems";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { totalItems, openCart } = useCart();
  const { wishlistCount, openWishlist } = useWishlist();
  const resourcesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setResourcesOpen(false);
    setMobileResourcesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (resourcesRef.current && !resourcesRef.current.contains(e.target as Node)) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-sm top-0" : "bg-transparent top-[40px]"
      }`}
    >
      {/* Top bar */}
      <div className={`text-center text-xs py-1.5 transition-colors duration-300 ${scrolled ? "bg-gray-900 text-gray-100" : "bg-black/40 text-white"}`}>
        Customer Support: <a href="tel:8666054975" className="font-semibold hover:underline">(866) 605-4975</a>
        &nbsp;|&nbsp;
        <a
          href="https://ironhavensupply.com"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold hover:underline"
        >
          Shop directly at IronHavenSupply.com
        </a>
        &nbsp;or explore your options below
      </div>

      <nav className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-flashlight-fill text-2xl text-amber-500"></i>
          </div>
          <span className={`font-extrabold text-lg tracking-tight transition-colors duration-300 ${scrolled ? "text-gray-900" : "text-white"}`}>
            Iron Haven Supply
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-5">
          {/* External Shopify collection links */}
          {shopLinks.map((item) => (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`text-sm font-medium hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? "text-gray-700" : "text-white"
              }`}
            >
              {item.label}
            </a>
          ))}

          {/* Compare Systems — amber highlight */}
          <Link
            to="/compare-backup-power-systems"
            className={`text-sm font-semibold hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1 ${
              scrolled ? "text-amber-600" : "text-amber-300"
            }`}
          >
            <i className="ri-equalizer-2-line text-base"></i>
            Compare
          </Link>

          {/* Resources dropdown */}
          <div className="relative" ref={resourcesRef}>
            <button
              onClick={() => setResourcesOpen((prev) => !prev)}
              className={`flex items-center gap-1 text-sm font-medium hover:text-amber-500 transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? "text-gray-700" : "text-white"
              }`}
            >
              Resources
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${resourcesOpen ? "rotate-180" : ""}`}></i>
            </button>

            {resourcesOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg border border-gray-100 py-1.5 shadow-lg z-50">
                {resourceLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setResourcesOpen(false)}
                    className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 transition-colors cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className={`${link.icon} text-base`}></i>
                    </div>
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Search icon */}
          <button
            onClick={() => setSearchOpen(true)}
            className={`w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer ${
              scrolled ? "text-gray-600 hover:bg-gray-100" : "text-white"
            }`}
            aria-label="Search"
          >
            <i className="ri-search-line text-lg"></i>
          </button>
        </div>

        {/* CTA + Cart */}
        <div className="hidden md:flex items-center gap-3">
          {/* Wishlist icon */}
          <button
            onClick={openWishlist}
            className={`relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer ${scrolled ? "text-gray-700 hover:bg-gray-100" : "text-white"}`}
            aria-label="Open wishlist"
          >
            <i className="ri-heart-line text-xl"></i>
            {wishlistCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] font-extrabold w-5 h-5 flex items-center justify-center rounded-full leading-none">
                {wishlistCount > 99 ? "99+" : wishlistCount}
              </span>
            )}
          </button>
          <button
            onClick={openCart}
            className={`relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer ${scrolled ? "text-gray-700 hover:bg-gray-100" : "text-white"}`}
            aria-label="Open cart"
          >
            <i className="ri-shopping-cart-2-line text-xl"></i>
            {totalItems > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[10px] font-extrabold w-5 h-5 flex items-center justify-center rounded-full leading-none">
                {totalItems > 99 ? "99+" : totalItems}
              </span>
            )}
          </button>
          <a
            href={FIND_MY_SYSTEM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-white px-5 py-2 rounded-md transition-colors whitespace-nowrap cursor-pointer"
          >
            Find My System
          </a>
        </div>

        {/* Mobile: search + cart + hamburger */}
        <div className="md:hidden flex items-center gap-1">
          <button
            onClick={() => setSearchOpen(true)}
            className={`w-9 h-9 flex items-center justify-center cursor-pointer ${scrolled ? "text-gray-900" : "text-white"}`}
            aria-label="Search"
          >
            <i className="ri-search-line text-xl"></i>
          </button>
          <button
            onClick={openCart}
            className={`relative w-9 h-9 flex items-center justify-center cursor-pointer ${scrolled ? "text-gray-900" : "text-white"}`}
            aria-label="Open cart"
          >
            <i className="ri-shopping-cart-2-line text-xl"></i>
            {totalItems > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[10px] font-extrabold w-5 h-5 flex items-center justify-center rounded-full leading-none">
                {totalItems > 99 ? "99+" : totalItems}
              </span>
            )}
          </button>
          <button
            className="flex items-center justify-center w-9 h-9 cursor-pointer"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl transition-colors duration-300 ${scrolled ? "text-gray-900" : "text-white"} ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          {/* External shop collection links */}
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-1 pt-1 pb-0.5">Shop by Category</p>
          {shopLinks.map((item) => (
            <a
              key={item.label}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-amber-500 transition-colors cursor-pointer py-2 px-1 rounded"
              onClick={() => setMenuOpen(false)}
            >
              <i className="ri-external-link-line text-gray-400 text-xs"></i>
              {item.label}
            </a>
          ))}

          <div className="border-t border-gray-100 mt-2 pt-2">
            <button
              className="w-full flex items-center justify-between text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-1 py-1 cursor-pointer"
              onClick={() => setMobileResourcesOpen((p) => !p)}
            >
              <span>Resources & Pages</span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileResourcesOpen ? "rotate-180" : ""}`}></i>
            </button>

            {mobileResourcesOpen && (
              <div className="flex flex-col gap-0.5 mt-1">
                {resourceLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 text-sm font-medium text-gray-700 hover:text-amber-500 hover:bg-amber-50 transition-colors cursor-pointer py-2 px-2 rounded"
                  >
                    <div className="w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className={`${link.icon} text-sm`}></i>
                    </div>
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <a
            href={FIND_MY_SYSTEM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-white px-5 py-2.5 rounded-md text-center transition-colors cursor-pointer whitespace-nowrap"
            onClick={() => setMenuOpen(false)}
          >
            Find My System
          </a>
        </div>
      )}

      {/* Search Overlay */}
      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
